export const DEFAULT_SHARE_IMAGE_URL =
  'https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/zigtruck_web_img.png';

interface KakaoShareRequest {
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  buttonTitle?: string;
}

/**
 * 텍스트를 클립보드에 복사한다.
 * Clipboard API는 https + 최신 브라우저에서만 동작하므로,
 * 인앱 브라우저 등 미지원 환경에서는 execCommand로 대체한다.
 * @returns 복사 성공 여부
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // execCommand 방식으로 재시도
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);

  try {
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch (error) {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
};

/**
 * 문자 메시지 앱을 미리 채워진 내용과 함께 실행한다.
 * iOS는 body 앞에 '&', 그 외에는 '?'를 구분자로 사용한다.
 */
export const openSmsApp = (message: string) => {
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const separator = isIOS ? '&' : '?';

  window.location.href = `sms:${separator}body=${encodeURIComponent(message)}`;
};

/**
 * 카카오톡 공유하기(피드 템플릿)를 실행한다.
 * @returns 공유창 실행 성공 여부
 */
export const shareToKakaoTalk = ({
  title,
  description,
  imageUrl,
  link,
  buttonTitle = '차량 보러가기',
}: KakaoShareRequest): boolean => {
  const Kakao = window.Kakao;
  // Kakao.Share 모듈은 Kakao.init() 이후에만 생성되므로, Share 여부가 아닌 SDK 로드 여부만 확인한다.
  if (!Kakao) {
    return false;
  }

  const linkTarget = { mobileWebUrl: link, webUrl: link };

  try {
    if (!Kakao.isInitialized()) {
      Kakao.init(process.env.KAKAO_KEY);
    }

    Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title,
        description,
        imageUrl,
        link: linkTarget,
      },
      buttons: [{ title: buttonTitle, link: linkTarget }],
    });

    return true;
  } catch (error) {
    return false;
  }
};
